import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  DraftOverrideCategory,
  IDraftRecommendation,
  useAcceptContractModelDraftRecommendationMutation,
  useConfirmContractModelDraftDocumentMutation,
  useGetContractModelDraftRunQuery,
  useOverrideContractModelDraftRecommendationMutation,
  usePresignContractModelDraftDocumentMutation,
  useRejectContractModelDraftRecommendationMutation,
  useRunContractModelDraftGenerationMutation,
} from "store/queries/contractModelDrafts";

import {
  ALLOWED_UPLOAD_EXTENSIONS,
  collectMissingInputs,
  confidenceSeverity,
  generateHint,
  groupByComponentType,
  isProcessing,
  isRunFinished,
  looksLikeMissingDocument,
  missingInputsOf,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
} from "./utils";

// Temporary: restrict this page to a single user while it's in early access.
const ALLOWED_EMAIL = "mike.over@clearhealthinc.com";

/** Ceiling on a single document upload. Contract PDFs/workbooks are small; a slow one is a broken one. */
const UPLOAD_TIMEOUT_MS = 120_000;

/** Pull a human-readable message out of an RTK Query error, a thrown Error, or anything else. */
const errorDetail = (e: unknown): string => {
  const apiMessage = (e as { data?: { message?: string } })?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.length > 0)
    return apiMessage;
  if (e instanceof Error && e.message) return e.message;
  return "Could not upload the file.";
};

const OVERRIDE_CATEGORIES: { label: string; value: DraftOverrideCategory }[] = [
  { label: "Agent misread the source", value: "agent_misread_source" },
  { label: "Agent missed a document", value: "agent_missed_document" },
  {
    label: "Correct read, wrong policy mapping",
    value: "wrong_policy_mapping",
  },
  { label: "Contract is ambiguous", value: "contract_ambiguous" },
];

function AttributionBadges({ rec }: { rec: IDraftRecommendation }) {
  if (rec.attributions.length === 0) {
    return <span className="text-500 text-sm">No source attribution</span>;
  }
  return (
    <div className="flex flex-column gap-3">
      {rec.attributions.map((a) => (
        <div key={a.id} className="flex flex-column gap-1">
          <div>
            <Tag
              className="white-space-nowrap"
              severity={a.verified ? "success" : "danger"}
              value={
                a.verified ? `verified (${a.verificationMethod})` : "unverified"
              }
            />
          </div>
          <blockquote className="m-0 pl-3 border-left-2 surface-border text-sm">
            <em>&ldquo;{a.exactQuote}&rdquo;</em>
          </blockquote>
          {a.locator ? (
            <span className="text-500 text-xs">Source: {a.locator}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function RecommendationCard({
  rec,
  onAccept,
  onReject,
  onOverride,
  busy,
}: {
  rec: IDraftRecommendation;
  onAccept: () => void;
  onReject: () => void;
  onOverride: () => void;
  busy: boolean;
}) {
  return (
    <Card className="mb-3">
      <div className="flex justify-content-between align-items-center mb-2">
        <div className="flex align-items-center gap-2">
          <span className="font-bold">{rec.componentType}</span>
          <Tag
            severity={confidenceSeverity(rec.confidence)}
            value={rec.confidence}
          />
          <Tag value={rec.status} />
        </div>
      </div>
      <div className="text-sm font-semibold mb-1">Proposed modeling</div>
      <pre
        className="surface-100 p-2 border-round text-sm overflow-auto"
        style={{ margin: 0 }}
      >
        {JSON.stringify(rec.proposedModeling, null, 2)}
      </pre>

      {missingInputsOf(rec).length > 0 ? (
        <div className="mt-3">
          <div className="text-sm font-semibold mb-1">
            Still needed for this component
          </div>
          <ul className="mt-0 mb-0 pl-3 text-sm text-700">
            {missingInputsOf(rec).map((item) => (
              <li key={item} className="mb-1">
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-3">
        <div className="text-sm font-semibold mb-1">Source language</div>
        <AttributionBadges rec={rec} />
      </div>
      {rec.override ? (
        <div className="mt-2 surface-50 p-2 border-round text-sm">
          <span className="font-semibold">
            Override ({rec.override.category}):
          </span>{" "}
          {rec.override.reason}
        </div>
      ) : null}
      <div className="flex gap-2 mt-3">
        <Button
          label="Accept"
          className="p-button-sm"
          onClick={onAccept}
          disabled={busy}
        />
        <Button
          label="Override"
          className="p-button-sm p-button-outlined p-button-warning"
          onClick={onOverride}
          disabled={busy}
        />
        <Button
          label="Reject"
          className="p-button-sm p-button-outlined p-button-danger"
          onClick={onReject}
          disabled={busy}
        />
      </div>
    </Card>
  );
}

function ReviewExplainer({ count }: { count: number }) {
  return (
    <div className="surface-100 border-round p-3 mb-3 text-sm">
      <div className="font-semibold mb-1">How to review this</div>
      <p className="mt-0 mb-2 text-500">
        The model read your documents and proposed {count} pieces of a
        ClearHealth pricing configuration. Nothing here is live — reviewing it{" "}
        <strong>never changes production pricing</strong>. Your job is to say,
        for each piece, whether the model read the contract correctly.
      </p>
      <ul className="mt-0 mb-0 pl-3 text-500">
        <li className="mb-1">
          Check the <strong>source language</strong> quoted under each proposal
          — a <strong>Verified</strong> badge only means the quote was found in
          the document, not that the interpretation is right.
        </li>
        <li className="mb-1">
          <strong>Accept</strong> keeps the proposal as-is;{" "}
          <strong>Override</strong> records what&rsquo;s wrong plus your
          correction (this is what improves the agent); <strong>Reject</strong>{" "}
          discards it.
        </li>
        <li>
          Low-confidence proposals and anything listed above as still needed are
          the places to look first.
        </li>
      </ul>
    </div>
  );
}

function MissingInputsPanel({ items }: { items: string[] }) {
  const documents = items.filter(looksLikeMissingDocument);
  const other = items.filter((i) => !looksLikeMissingDocument(i));
  return (
    <div
      className="surface-card border-1 border-round p-3 mb-4"
      style={{ borderColor: "var(--orange-500)" }}
    >
      <div className="flex align-items-center gap-2 mb-2">
        <i className="pi pi-exclamation-triangle text-orange-500" />
        <span className="font-semibold">
          This contract can&rsquo;t be fully modeled yet &mdash; {items.length}{" "}
          input{items.length === 1 ? "" : "s"} still needed
        </span>
      </div>
      <p className="text-500 text-sm mt-0 mb-3">
        The model could not determine these from the uploaded documents. Gather
        them, then start a <strong>new run</strong> with the fuller set &mdash;
        this run stays as a record.
      </p>

      {documents.length > 0 ? (
        <div className="mb-3">
          <div className="font-medium text-sm mb-1">Documents to provide</div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {documents.map((d) => (
              <li key={d} className="mb-1">
                {d}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {other.length > 0 ? (
        <div>
          <div className="font-medium text-sm mb-1">
            Other information needed
          </div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {other.map((o) => (
              <li key={o} className="mb-1">
                {o}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function ContractModelDraftDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useUserWithAccount();
  const isAllowed = (userInfo?.email ?? "").toLowerCase() === ALLOWED_EMAIL;
  const toastRef = useContext(ToastContext);

  const { data: run } = useGetContractModelDraftRunQuery(id, {
    skip: !isAllowed || !id,
    // Poll while the agents-app worker is processing so the UI reflects progress.
    pollingInterval: id ? 4000 : 0,
  });

  const [presignDoc] = usePresignContractModelDraftDocumentMutation();
  const [confirmDoc] = useConfirmContractModelDraftDocumentMutation();
  const [runGeneration, { isLoading: generating }] =
    useRunContractModelDraftGenerationMutation();
  const [accept, { isLoading: accepting }] =
    useAcceptContractModelDraftRecommendationMutation();
  const [reject, { isLoading: rejecting }] =
    useRejectContractModelDraftRecommendationMutation();
  const [override, { isLoading: overriding }] =
    useOverrideContractModelDraftRecommendationMutation();
  const busy = accepting || rejecting || overriding;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Name of the file currently uploading, or null. Doubles as the progress indicator's label.
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const uploading = uploadingFile !== null;
  const [overrideTarget, setOverrideTarget] =
    useState<IDraftRecommendation | null>(null);
  const [overrideCategory, setOverrideCategory] =
    useState<DraftOverrideCategory>("wrong_policy_mapping");
  const [overrideReason, setOverrideReason] = useState("");

  const grouped = useMemo(
    () => groupByComponentType(run?.recommendations ?? []),
    [run]
  );

  // Everything the model said it still needs, rolled up from the individual recommendations.
  const missingInputs = useMemo(
    () => collectMissingInputs(run?.recommendations ?? []),
    [run]
  );

  // Once a run has produced recommendations it is a finished record: uploading more documents or
  // re-generating would append a second set of recommendations to it. Start a new run instead.
  const finished = isRunFinished(run?.recommendations ?? []);

  const hint = run
    ? generateHint(run.documents.length, run.status, { uploading, finished })
    : null;

  const toast = (
    severity: "success" | "error",
    summary: string,
    detail: string
  ) => toastRef?.current?.show({ severity, summary, detail, life: 5000 });

  const handleUpload = async (file: File) => {
    const parts = file.name.toLowerCase().split(".");
    const ext = parts.length > 1 ? parts[parts.length - 1] : "";
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
      toast(
        "error",
        "Unsupported file type",
        `.${
          ext || "?"
        } isn't supported. Allowed: ${ALLOWED_UPLOAD_EXTENSIONS.join(", ")}.`
      );
      return;
    }
    const contentType = file.type || "application/octet-stream";
    // A PUT straight to S3 has no built-in deadline: a stalled connection hangs forever, which is
    // how an upload could sit "in progress" indefinitely with the button stuck disabled.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), UPLOAD_TIMEOUT_MS);
    try {
      setUploadingFile(file.name);
      const { uploadUrl, documentId } = await presignDoc({
        runId: id,
        fileName: file.name,
        contentType,
      }).unwrap();

      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
        signal: abort.signal,
      });
      if (!res.ok) throw new Error(`S3 rejected the upload (${res.status}).`);

      // Don't trust the PUT alone — ask the API to HEAD the object. This is what turns a silently
      // failed upload into an error you can see, instead of a run that dies 20 minutes later.
      await confirmDoc({ runId: id, documentId }).unwrap();
      toast("success", "Document uploaded", file.name);
    } catch (e) {
      const detail =
        e instanceof DOMException && e.name === "AbortError"
          ? `Upload timed out after ${
              UPLOAD_TIMEOUT_MS / 1000
            }s. Check your connection and try again.`
          : errorDetail(e);
      toast("error", `Couldn't upload ${file.name}`, detail);
    } finally {
      clearTimeout(timer);
      setUploadingFile(null);
    }
  };

  const submitOverride = async () => {
    if (!overrideTarget) return;
    await override({
      recommendationId: overrideTarget.id,
      category: overrideCategory,
      reason: overrideReason,
      agentWasWrong: true,
    }).unwrap();
    setOverrideTarget(null);
    setOverrideReason("");
  };

  if (!isAllowed) return <Navigate to="/" replace />;

  return (
    <div className="p-4">
      <Button
        label="Back to runs"
        icon="pi pi-arrow-left"
        className="p-button-text p-button-sm mb-2 pl-0"
        onClick={() => navigate("/contract-model-drafts")}
      />

      {!run ? (
        <p className="text-500">Loading run…</p>
      ) : (
        <>
          <div className="flex align-items-center gap-2 mb-1">
            <h1 className="m-0">{runDisplayName(run)}</h1>
            <Tag
              value={runStatusLabel(run.status)}
              severity={runStatusSeverity(run.status)}
            />
          </div>
          <p className="text-500 text-sm mt-0 mb-3">
            Upload the raw payer-contract documents, generate a model, then
            review each recommendation.
          </p>

          {missingInputs.length > 0 ? (
            <MissingInputsPanel items={missingInputs} />
          ) : null}

          <div className="surface-card border-1 surface-border border-round p-3 mb-4">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="font-semibold">
                Documents ({run.documents.length})
              </span>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(",")}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                label={uploading ? "Uploading…" : "Upload document"}
                icon="pi pi-upload"
                className="p-button-outlined p-button-sm"
                loading={uploading}
                disabled={uploading || finished}
                onClick={() => fileInputRef.current?.click()}
              />
            </div>

            {run.documents.length === 0 && !uploading ? (
              <p className="text-500 text-sm m-0">
                No documents yet — add the contract PDF/Excel to model from.
              </p>
            ) : (
              <ul className="list-none p-0 m-0">
                {run.documents.map((d) => (
                  <li key={d.id} className="text-sm py-1">
                    <i className="pi pi-file mr-2 text-500" />
                    {d.fileName}
                  </li>
                ))}
                {uploadingFile ? (
                  <li className="text-sm py-1 text-500">
                    <i className="pi pi-spin pi-spinner mr-2" />
                    Uploading {uploadingFile}…
                  </li>
                ) : null}
              </ul>
            )}

            <div className="flex align-items-center gap-2 mt-3">
              <Button
                label={
                  isProcessing(run.status) ? "Generating…" : "Generate model"
                }
                icon="pi pi-bolt"
                disabled={
                  generating ||
                  finished ||
                  uploading ||
                  run.documents.length === 0 ||
                  isProcessing(run.status)
                }
                onClick={() => runGeneration(run.id)}
              />
              {hint ? <span className="text-500 text-sm">{hint}</span> : null}
            </div>
          </div>

          <h3 className="mt-0 mb-2">Recommendations</h3>
          {finished ? (
            <ReviewExplainer count={run.recommendations.length} />
          ) : null}
          {run.recommendations.length === 0 ? (
            <p className="text-500">
              {isProcessing(run.status)
                ? "Working on it… recommendations will appear here when the model finishes."
                : "None yet. Add documents above and click “Generate model”."}
            </p>
          ) : (
            Object.entries(grouped).map(([type, recs]) => (
              <div key={type} className="mb-4">
                <h4 className="mt-0 mb-2">{type}</h4>
                {recs.map((rec) => (
                  <RecommendationCard
                    key={rec.id}
                    rec={rec}
                    busy={busy}
                    onAccept={() => accept({ recommendationId: rec.id })}
                    onReject={() => reject(rec.id)}
                    onOverride={() => setOverrideTarget(rec)}
                  />
                ))}
              </div>
            ))
          )}
        </>
      )}

      <Dialog
        header="Override recommendation"
        visible={!!overrideTarget}
        style={{ width: "40rem" }}
        onHide={() => setOverrideTarget(null)}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label
              className="block mb-2 font-medium"
              htmlFor="override-category"
            >
              Why is the agent wrong?
            </label>
            <Dropdown
              id="override-category"
              className="w-full"
              options={OVERRIDE_CATEGORIES}
              value={overrideCategory}
              onChange={(e) => setOverrideCategory(e.value)}
            />
          </div>
          <div>
            <label className="block mb-2 font-medium" htmlFor="override-reason">
              Correction / reason
            </label>
            <InputTextarea
              id="override-reason"
              className="w-full"
              rows={4}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setOverrideTarget(null)}
            />
            <Button
              label="Save override"
              icon="pi pi-check"
              disabled={busy || !overrideReason.trim()}
              onClick={submitOverride}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
